import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  field,
  getObject,
  mutationHeaders,
  type GetPath,
  type Row,
  type Schema,
} from './api/client';
import { apiError } from './api/session';
import { useAccess, useRows } from './api/data';
import { fieldLabel } from './i18n';
import {
  Button,
  Empty,
  ErrorState,
  Field,
  FilePicker,
  Loading,
  Status,
  Table,
  type Column,
} from './ui';

export type EmployeeKind =
  'documents' | 'compensation' | 'payroll' | 'screenings' | 'right-to-work';
const records: Record<
  EmployeeKind,
  { title: string; fields: string[]; read: string; write: string }
> = {
  documents: {
    title: 'Документы',
    fields: ['name', 'type', 'expiresAt', 'status'],
    read: 'documents',
    write: 'documents',
  },
  compensation: {
    title: 'Вознаграждение',
    fields: ['amount', 'frequency', 'effectiveDate', 'reason'],
    read: 'compensation',
    write: 'compensation',
  },
  payroll: {
    title: 'Расчёт зарплаты',
    fields: ['payrollGroup', 'providerReferenceMasked', 'effectiveFrom', 'effectiveTo'],
    read: 'payroll',
    write: 'payroll',
  },
  screenings: {
    title: 'Проверки',
    fields: ['type', 'status', 'completedAt', 'externalReference'],
    read: 'screening',
    write: 'screening',
  },
  'right-to-work': {
    title: 'Право на работу',
    fields: ['country', 'basisType', 'status', 'checkedAt', 'expiresOn'],
    read: 'screening',
    write: 'screening',
  },
};
function value(row: Row, key: string) {
  const current = row[key];
  if (current === null || current === undefined) return '—';
  if (key === 'status' || key === 'frequency') return <Status value={String(current)} />;
  if (key === 'amount' && typeof current === 'object') {
    const money = current as { amount?: string | number; currency?: string };
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: money.currency || 'RUB',
    }).format(Number(money.amount || 0));
  }
  if (
    key.endsWith('At') ||
    key.endsWith('On') ||
    key.endsWith('Date') ||
    key.startsWith('effective')
  )
    return new Date(String(current)).toLocaleDateString('ru-RU');
  return String(current);
}
export function EmployeeRecords({ employeeId, kind }: { employeeId: string; kind: EmployeeKind }) {
  const access = useAccess();
  const path = `/api/v1/employees/{employeeId}/${kind}` as GetPath;
  const permission = records[kind];
  const canRead = access.can(`employee_fields.${permission.read}.read`);
  const list = useRows(path, {}, { params: { employeeId }, enabled: canRead });
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const fileRows = useQuery({
    queryKey: [
      'employee-document-files',
      employeeId,
      list.rows.map((row) => field(row, 'fileId')).join(','),
    ],
    enabled: kind === 'documents' && list.rows.length > 0,
    queryFn: ({ signal }) =>
      Promise.all(
        list.rows.map(async (row) => ({
          ...row,
          file: await getObject<Schema<'FileResponse'>>(
            '/api/v1/files/{id}',
            { id: field(row, 'fileId') },
            signal,
          ),
        })),
      ),
    refetchInterval: (query) =>
      query.state.data?.some(
        (row) => !['CLEAN', 'INFECTED', 'QUARANTINED', 'SCAN_FAILED'].includes(row.file.status),
      )
        ? 2000
        : false,
  });
  if (!canRead && !access.isLoading) return <Empty title="Нет доступа к этим данным" />;
  const rows: Row[] =
    kind === 'documents'
      ? fileRows.data?.map((row) => ({
          ...row,
          name: row.file.name,
          status: row.file.status,
          fileVersionId: row.file.fileVersionId,
        })) || list.rows
      : list.rows;
  const columns: Column<Row>[] = permission.fields.map((key) => ({
    key,
    label: fieldLabel(key),
    width: key === 'name' || key === 'reason' ? 280 : 180,
    render: (row) => value(row, key),
  }));
  if (kind === 'documents')
    columns.push({
      key: 'download',
      label: '',
      width: 72,
      filter: false,
      sortable: false,
      render: (row) => (
        <Button
          icon="ChevronDown"
          disabled={field(row, 'status') !== 'CLEAN'}
          aria-label="Скачать документ"
          onClick={async () => {
            try {
              const result = await api.POST('/api/v1/files/{id}/download-url', {
                params: { path: { id: field(row, 'fileId') }, header: mutationHeaders() },
                body: { fileVersionId: field(row, 'fileVersionId') },
              });
              if (!result.data) throw apiError(result.response, result.error);
              const link = document.createElement('a');
              link.href = result.data.url;
              link.download = field(row, 'name');
              link.click();
            } catch (cause) {
              setError(cause as Error);
            }
          }}
        />
      ),
    });
  return (
    <div className="employee-records">
      <div className="table-toolbar">
        <h2>{permission.title}</h2>
        <span className="toolbar-spacer" />
        {access.can(`employee_fields.${permission.write}.write`) && (
          <Button
            icon="Plus"
            onClick={() =>
              void navigate({
                to: '/employees/profile/$employeeId/$operation',
                params: {
                  employeeId,
                  operation: kind === 'documents' ? 'add-document' : 'add-record',
                },
                search: { kind },
              })
            }
          >
            Добавить
          </Button>
        )}
      </div>
      {error && <ErrorState error={error} />}{' '}
      {list.error || fileRows.error ? (
        <ErrorState error={list.error || fileRows.error} />
      ) : (
        <Table
          rows={rows}
          columns={columns}
          loading={list.isLoading || fileRows.isLoading}
          empty="Записей пока нет"
        />
      )}
      {list.hasNextPage && (
        <Button
          className="see-all"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          {list.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
        </Button>
      )}
    </div>
  );
}
export function DocumentUpload({
  employee,
  onClose,
  onSuccess,
}: {
  employee: Schema<'EmployeeResponse'>;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [stage, setStage] = useState('');
  const [error, setError] = useState<Error | null>(null);
  return (
    <section className="form-page">
      <div className="form-page-heading">
        <h1>Добавить документ</h1>
      </div>
      <form
        className="domain-form"
        onSubmit={async (event) => {
          event.preventDefault();
          const values = new FormData(event.currentTarget);
          const file = values.get('file');
          if (!(file instanceof File) || !file.size) return;
          setPending(true);
          setError(null);
          try {
            const data = new FormData();
            data.set('purpose', 'documents');
            data.set('entityType', 'Employee');
            data.set('entityId', employee.id);
            const name = String(values.get('name') || file.name);
            const extension = file.name.match(/\.[^.]+$/)?.[0] || '';
            data.set(
              'file',
              file,
              name.toLowerCase().endsWith(extension.toLowerCase()) ? name : name + extension,
            );
            setStage('Загрузка документа');
            const uploaded = await api.POST('/api/v1/files', {
              params: { header: mutationHeaders() },
              body: data as never,
              bodySerializer: (value) => value as unknown as FormData,
            });
            if (!uploaded.data) throw apiError(uploaded.response, uploaded.error);
            setStage('Проверка безопасности файла');
            let metadata = uploaded.data;
            for (
              let attempts = 0;
              attempts < 90 &&
              !['CLEAN', 'INFECTED', 'SCAN_FAILED', 'QUARANTINED'].includes(metadata.status);
              attempts++
            ) {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              metadata = await getObject<Schema<'FileResponse'>>('/api/v1/files/{id}', {
                id: metadata.id,
              });
            }
            if (metadata.status !== 'CLEAN')
              throw new Error(
                metadata.status === 'INFECTED'
                  ? 'Проверка выявила угрозу в файле'
                  : 'Проверка файла ещё не завершена. Файл сохранён; попробуйте прикрепить его позже.',
              );
            const latest = await getObject<Schema<'EmployeeResponse'>>(
              '/api/v1/employees/{employeeId}',
              { employeeId: employee.id },
            );
            const attached = await api.POST('/api/v1/employees/{employeeId}/documents', {
              params: { path: { employeeId: employee.id }, header: mutationHeaders() },
              body: {
                version: latest.version,
                fileId: metadata.id,
                type: String(values.get('type')),
                expiresAt: String(values.get('expiresAt') || '') || null,
              },
            });
            if (!attached.data) throw apiError(attached.response, attached.error);
            onSuccess();
          } catch (cause) {
            setError(cause as Error);
          } finally {
            setPending(false);
          }
        }}
      >
        <Field label="Название файла" name="name" placeholder="Например, трудовой договор.pdf" />
        <Field label="Тип документа" name="type" required />
        <Field label="Срок действия" name="expiresAt" type="date" />
        <FilePicker
          name="file"
          label="Перетащите файл или нажмите, чтобы выбрать"
          description="PDF, PNG, JPG · до 20 МБ"
          accept="application/pdf,image/png,image/jpeg"
          required
        />
        {pending && (
          <p role="status" className="muted">
            {stage}
          </p>
        )}
        {error && <ErrorState error={error} />}
        <footer>
          <Button onClick={onClose} disabled={pending}>
            Отмена
          </Button>
          <Button type="submit" className="primary" disabled={pending}>
            {pending ? 'Подождите…' : 'Добавить документ'}
          </Button>
        </footer>
      </form>
    </section>
  );
}
export function EmployeeRoleProfile({ employee }: { employee: Schema<'EmployeeResponse'> }) {
  const access = useAccess();
  const navigate = useNavigate();
  const resolution = useQuery({
    queryKey: ['role-resolution', employee.id],
    enabled: access.can('work_plans.read') || access.can('work_plans.manage'),
    queryFn: ({ signal }) =>
      getObject<Record<string, unknown>>(
        '/api/v1/employees/{id}/role-profile-resolution',
        { id: employee.id },
        signal,
      ),
  });
  if (!access.can('work_plans.read') && !access.can('work_plans.manage') && !access.isLoading)
    return null;
  const current = resolution.data?.currentAssignment as
    { validFrom: string; profile: { mission: string } } | undefined;
  const suggested = resolution.data?.suggestedVersion as
    { id: string; versionNumber: number } | undefined;
  const canAssign =
    Array.isArray(resolution.data?.allowedActions) &&
    resolution.data.allowedActions.includes('assign');
  return (
    <section className="panel">
      <header>
        <h2>Профиль должности</h2>
      </header>
      <div className="role-assignment-body">
        {resolution.isLoading ? (
          <Loading />
        ) : resolution.error ? (
          <ErrorState error={resolution.error} />
        ) : (
          <>
            <Status value={String(resolution.data?.status || 'NOT_CONFIGURED')} />
            {current && (
              <>
                <p>{current.profile.mission}</p>
                <p className="muted">
                  Назначен с {new Date(current.validFrom).toLocaleDateString('ru-RU')}
                </p>
              </>
            )}
            {resolution.data?.updateAvailable && <p>Доступна новая версия профиля должности.</p>}
            <p className="muted">
              Опубликованный профиль определяет задачи и навыки для рабочих планов и оценки
              сотрудника.
            </p>
            <div className="entity-actions">
              <Link
                className="pill"
                to="/employees/profile/$employeeId/$operation"
                params={{ employeeId: employee.id, operation: 'role-profile-history' }}
              >
                История назначений
              </Link>
              {suggested && access.can('performance_templates.read') && (
                <Link
                  className="pill"
                  to="/employees/profile/$employeeId/$operation"
                  params={{ employeeId: employee.id, operation: 'role-profile-preview' }}
                  search={{ versionId: suggested.id }}
                >
                  Посмотреть профиль
                </Link>
              )}
              {canAssign && (
                <Button
                  onClick={() =>
                    void navigate({
                      to: '/employees/profile/$employeeId/$operation',
                      params: { employeeId: employee.id, operation: 'role-profile' },
                    })
                  }
                >
                  Назначить профиль
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
